<template>
  <div
    v-if="sidebarComponents.length === 0"
    class="project-section pt-60 pb-120 tw-overflow-hidden"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div class="container">
      <h2 class="tw-mb-8 tw-self-center tw-text-center">{{ vars.titleText }}</h2>
      <div class="row">
        <div class="project-slider-2 owl-carousel">
          <div
            v-for="(card, index) in guides"
            :key="index"
            class="imageCard -type-2 tw-cursor-pointer"
            @click.prevent="handleCardClick(card)"
          >
            <div>
              <library-image :src="card.photo" :w-ratio="350" :h-ratio="622" :alt="card.title" />
            </div>
            <div class="tw-h-[80px]">
              <h5 class="imageCard__title tw-line-clamp-2">{{ card.title }}</h5>
            </div>
            <div class="mt-4">
              <div class="mt-4">
                <div class="mt-4" v-if="card.button_label">
                  <button v-if="card.button_label" @click.stop="handleButtonClick(card)" :class="`n-primary`">
                    {{ card.button_label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <popup v-if="selectedCard && popupConfig.isActive" :config="popupConfig" custom-width="90%">
      <template #content>
        <library-form v-if="selectedCard.formData" :form="selectedCard.formData" version="v2" :component="component" />
      </template>
    </popup>
  </div>

  <div
    v-else-if="sidebarComponents.length > 0"
    class="tw-p-4"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <h4 class="tw-mb-4">{{ vars.titleText }}</h4>
    <Swiper
      :modules="[SwiperPagination]"
      :pagination="false"
      :slides-per-view="1"
      :space-between="24"
      effect="fade"
      :clickable="true"
      :watch-overflow="true"
      @slideChange="onSlideChange"
      @swiper="onSwiper"
      class="tw-w-full lg:tw-max-w-full"
    >
      <SwiperSlide v-for="(card, index) in guides" :key="`guide-${index}`">
        <div @click.prevent="handleCardClick(card)">
          <div>
            <library-image
              :src="card.photo === null ? undefined : String(card.photo)"
              :w-ratio="3"
              :h-ratio="4.5"
              :alt="card.title"
            />
          </div>
          <div class="tw-mt-4 tw-h-[64px]">
            <h5 class="tw-line-clamp-3">{{ card.title }}</h5>
          </div>
          <div class="mt-4" v-if="card.button_label">
            <button @click.stop="handleButtonClick(card)" class="n-primary">
              {{ card.button_label }}
            </button>
          </div>
        </div>
      </SwiperSlide>
    </Swiper>

    <div v-if="swiper" class="tw-mt-4">
      <LibrarySliderPagination
        :swiper="swiper"
        variant="v8"
        :can-paginate-back="canPaginateBack"
        :can-paginate-next="canPaginateNext"
      />
    </div>

    <popup v-if="selectedCard && popupConfig.isActive" :config="popupConfig" custom-width="90%">
      <template #content>
        <div class="tw-grid tw-grid-cols-12 tw-gap-12 lg:tw-items-start">
          <div class="tw-col-span-12 lg:tw-col-span-4">
            <div class="">
              <library-image
                :src="selectedCard.photo === null ? undefined : String(selectedCard.photo)"
                :w-ratio="4"
                :h-ratio="7"
                :alt="selectedCard.title"
              />
            </div>
          </div>
          <div class="tw-col-span-12 lg:tw-col-span-8 lg:tw-max-h-[700px] lg:tw-overflow-y-auto">
            <library-form
              v-if="selectedCard.formData"
              :form="selectedCard.formData"
              version="v2"
              :component="component"
              :is-form-name-visible="false"
            />
          </div>
        </div>
      </template>
    </popup>
  </div>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
import { useWebsiteStore } from '~/units/website/store';
import Popup from '~/components/common/popup/Popup.vue';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

export default defineNuxtComponent({
  name: 'OurGuide',
  mixins: [ComponentMixin],
  components: { Popup },

  setup() {
    return {
      SwiperPagination: Pagination,
    };
  },

  data() {
    return {
      swiper: null as SwiperType | null,
      canPaginateBack: true,
      canPaginateNext: true,
      variants: {
        v1: 'v1',
        v2: 'v2',
      },
      selectedCard: null as any,
      popupConfig: { isActive: false },
    };
  },

  mounted() {
    if (this.swiper) {
      this.onSlideChange();
    }
  },

  watch: {
    guides(newVal) {
      if (newVal.length > 0) {
        this.$nextTick(() => {
          this.initCarousel();
        });
      }
    },
  },

  computed: {
    sidebarComponents() {
      const websiteStore = useWebsiteStore();
      return websiteStore.sidebarComponents;
    },

    guides() {
      return this.groupedVariables.guides;
    },

    buttons() {
      return this.groupedVariables.buttons;
    },
  },

  methods: {
    handleCardClick(card: any) {
      this.processCardAction(card);
    },

    initCarousel() {
      if (typeof window !== 'undefined' && (window as any).$ && (window as any).$.fn.owlCarousel) {
        const $ = (window as any).$;
        $('.project-slider-2').owlCarousel('destroy'); // Fix selector

        const responsiveSettings =
          this.sidebarComponents.length > 0
            ? { 0: { items: 1 } }
            : {
                0: { items: 1 },
                768: { items: 2 },
              };

        $('.project-slider-2').owlCarousel({
          loop: true,
          margin: 30,
          nav: true,
          dots: false,
          navText: ["<i class='las la-arrow-left'></i>", "<i class='las la-arrow-right'></i>"],
          responsive: responsiveSettings,
        });
      } else {
        console.warn('jQuery or Owl Carousel not loaded');
      }
    },

    handleButtonClick(card: any) {
      this.processCardAction(card);
    },

    onSwiper(swiper: SwiperType) {
      this.swiper = swiper;
      this.onSlideChange();
    },

    onSlideChange() {
      if (!this.swiper) return;
      const isAtStart = this.swiper.isBeginning;
      const isAtEnd = this.swiper.isEnd;
      if (isAtStart) this.canPaginateBack = false;
      else this.canPaginateBack = true;
      if (isAtEnd) this.canPaginateNext = false;
      else this.canPaginateNext = true;
    },

    processCardAction(card: any) {
      const hasLink = !!card.button_link;
      const rawForm = card.guide || card.form;

      if (hasLink) {
        window.open(card.button_link, '_blank');
        return;
      }

      if (!rawForm) return;

      const formData = this.parseFormV2(rawForm);
      if (!formData) return;

      this.selectedCard = { ...card, formData };
      this.popupConfig.isActive = true;
    },
  },
});
</script>

<style scoped>
::v-deep(.v8-arrows) {
  justify-content: flex-start;
}
</style>
